import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Flame, X } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Public worldwide-remembrance search bar shown on the Home page.
 * Queries `/api/v1/memorials/search?q=...&country=...` — only PUBLIC memorials.
 * Anonymous — no auth required.
 */
export default function MemorialSearch() {
  const [q, setQ] = useState("");
  const [country, setCountry] = useState("");
  const [results, setResults] = useState(null); // null = idle, [] = searched
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function submit(e) {
    e.preventDefault();
    if (!q.trim() && !country.trim()) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(`${API}/v1/memorials/search`, {
        params: { q: q.trim(), country: country.trim() || undefined, limit: 12 },
      });
      setResults(data.results || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Something went wrong. Please try again.");
      setResults([]);
    } finally {
      setLoading(false);
    }
  }

  function clear() {
    setQ("");
    setCountry("");
    setResults(null);
    setError("");
  }

  return (
    <section
      id="memorial-search"
      data-testid="memorial-search-section"
      className="relative max-w-[1400px] mx-auto px-6 md:px-10 py-14 md:py-20"
    >
      <div className="text-center mb-8 md:mb-10">
        <div className="flourish w-56 md:w-72 mx-auto" />
        <h2 className="mt-4 heritage-title text-3xl md:text-4xl lg:text-5xl leading-tight">
          Worldwide Remembrance
        </h2>
        <p className="mt-3 max-w-xl mx-auto font-italic-serif italic text-[color:var(--ink-soft)]">
          "Search a name across every family archive. If they have been
          remembered, we will help you find them."
        </p>
      </div>

      <form
        onSubmit={submit}
        data-testid="memorial-search-form"
        className="max-w-3xl mx-auto border border-[color:var(--sepia)]/30 bg-[color:var(--parchment)] shadow-[0_20px_50px_-30px_rgba(43,28,15,0.35)]"
      >
        <div className="grid md:grid-cols-[1.5fr_1fr_auto] items-stretch">
          <label className="relative px-5 py-4 border-b md:border-b-0 md:border-r border-[color:var(--sepia)]/25">
            <span className="overline">Name</span>
            <input
              data-testid="memorial-search-name"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="e.g. Margaret Bennett"
              className="mt-1 w-full bg-transparent text-lg font-heritage-caps tracking-wide focus:outline-none placeholder:text-[color:var(--sepia)]/40"
            />
          </label>
          <label className="relative px-5 py-4 border-b md:border-b-0 md:border-r border-[color:var(--sepia)]/25">
            <span className="overline">Country or place</span>
            <input
              data-testid="memorial-search-country"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              placeholder="e.g. England"
              className="mt-1 w-full bg-transparent text-lg font-heritage-caps tracking-wide focus:outline-none placeholder:text-[color:var(--sepia)]/40"
            />
          </label>
          <button
            type="submit"
            disabled={loading}
            data-testid="memorial-search-submit"
            className="btn-wood rounded-none border-0 justify-center px-8 disabled:opacity-70"
          >
            <Search size={16} /> {loading ? "Searching…" : "Search"}
          </button>
        </div>
      </form>

      {/* Results */}
      <AnimatePresence>
        {results !== null && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-3xl mx-auto mt-8"
            data-testid="memorial-search-results"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="numeric-index text-[color:var(--ink-soft)]">
                {loading
                  ? "…"
                  : `${results.length} public memorial${results.length === 1 ? "" : "s"} found`}
              </div>
              <button
                onClick={clear}
                data-testid="memorial-search-clear"
                className="inline-flex items-center gap-1 text-xs link-underline text-[color:var(--ink-soft)]"
              >
                <X size={12} /> Clear
              </button>
            </div>

            {error && (
              <div className="text-sm text-[color:var(--rust)] border-l-2 border-[color:var(--rust)] pl-3 py-1 mb-4">
                {error}
              </div>
            )}

            {results.length === 0 && !error && !loading && (
              <p className="text-center font-italic-serif italic text-[color:var(--ink-soft)] border border-dashed border-[color:var(--ink-line-strong)] p-8">
                No public memorials matched. Try a different name — or a family
                may have kept theirs private, which is also a kind of
                remembering.
              </p>
            )}

            <ul className="divide-y divide-[color:var(--sepia)]/20 border border-[color:var(--sepia)]/20 bg-[color:var(--parchment)]">
              {results.map((r) => {
                const name = [r.first_name, r.last_name].filter(Boolean).join(" ") || "Unnamed";
                const years = [r.birth_date, r.death_date].filter(Boolean).join(" – ");
                const place = r.death_place || r.birth_place;
                return (
                  <li key={r.memorial_id}>
                    <a
                      href={`/memorial/${r.memorial_id}`}
                      data-testid={`memorial-search-result-${r.memorial_id}`}
                      className="flex items-center gap-4 px-5 py-4 hover:bg-[color:var(--parchment-2)]/60 transition-colors"
                    >
                      <Flame size={18} className="text-[color:var(--rust)] shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="heritage-title text-lg md:text-xl leading-tight truncate">
                          {name}
                        </div>
                        <div className="numeric-index text-[color:var(--ink-soft)] mt-0.5 truncate">
                          {[years, place].filter(Boolean).join(" · ") || "Details private"}
                        </div>
                      </div>
                      <div className="hidden sm:flex items-center gap-3 numeric-index text-[color:var(--ink-soft)] shrink-0">
                        <span>{r.candle_count} candles</span>
                        <span>·</span>
                        <span>{r.message_count} messages</span>
                      </div>
                    </a>
                  </li>
                );
              })}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
